import { type FC, type HTMLAttributes, type ReactNode, type Ref } from "react";
import { cn } from "../lib/utils";
import { trackingAttrs } from "../utils";

export type ListProps = HTMLAttributes<HTMLUListElement> & {
	trackingId?: string;
	ref?: Ref<HTMLUListElement>;
};

/**
 * `index.css` re-styles bare `ul`/`ol` for prose, so neutralize its margin/padding here and let
 * a `List` lay out from its own classes. The marker reset lives on `List.Item` instead — the
 * global rule targets the `<li>` directly, so setting it on the `<ul>` has no effect.
 */
const Root: FC<ListProps> = ({ trackingId, className, ref, ...rest }) => (
	<ul
		ref={ref}
		className={cn("m-0 p-0 space-y-2", className)}
		{...trackingAttrs(trackingId)}
		{...rest}
	/>
);
Root.displayName = "List";

export type ListItemProps = HTMLAttributes<HTMLLIElement> & {
	leading?: ReactNode;
	trackingId?: string;
	ref?: Ref<HTMLLIElement>;
};

const Item: FC<ListItemProps> = ({ leading, trackingId, className, children, ref, ...rest }) => (
	<li
		ref={ref}
		className={cn("flex list-none items-center gap-2", className)}
		{...trackingAttrs(trackingId)}
		{...rest}
	>
		{leading != null && <span className="shrink-0 flex items-center">{leading}</span>}
		{children}
	</li>
);
Item.displayName = "List.Item";

export const List = Object.assign(Root, { Item });
